import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

// Updated variants based on Locuno Design System
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-primary font-bold uppercase ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-y-0.5",
  {
    variants: {
      variant: {
        // Primary Action: Solid Vintage Coral (Secondary Color)
        primary: `bg-secondary text-secondary-foreground hover:bg-secondary-hover shadow-md rounded-lg`, // Using secondary color as primary button
        // Secondary Action: Transparent Glassmorphic
        secondary: `glass text-foreground hover:bg-accent/20 border-0 rounded-lg`, // Glass effect, hover with mustard highlight
        // Default shadcn mapped to our primary action color (Dark Navy)
        default: `bg-primary-action text-primary-foreground hover:bg-primary-hover_action shadow-sm rounded-lg`,
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground rounded-lg", // Keep outline standard
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-lg", // Keep ghost standard
        link: "text-primary underline-offset-4 hover:underline", // Keep link standard
      },
      size: {
        // Use design system button sizes
        default: `h-12 px-6 text-button rounded-lg`, // 48px height approx, 16px text
        sm: `h-10 px-4 text-sm rounded-md`, // Smaller size
        lg: `h-14 px-8 text-lg rounded-xl`, // Larger size
        icon: "h-12 w-12 rounded-lg", // 48px square
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary", // Default to our primary style (Vintage Coral)
      size: "default",
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild = false, isLoading, leftIcon, rightIcon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={isLoading || props.disabled} // Combine disabled states
        {...props}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-8 w-8" // Adjusted size
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <>
            {leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="ml-2">{rightIcon}</span>}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
